import { useEffect, useRef, useState, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/api";
import PostCard from "../components/PostCard";
import SiteFooter from "../components/SiteFooter";
import { VerdictStamp } from "../components/Verdict";
import { ordinalDate } from "../utils/date";
import { stripHtml } from "../utils/stripHtml";
import useMeta from "../hooks/useMeta";
import useJsonLd from "../hooks/useJsonLd";
import { cloudinaryImg } from "../utils/cloudinary";
import "./Home.css";

export default function Home({ user }) {
  useMeta({
    description: "Independent reviews of indie games worth writing about. No sponsored content, no scores — just honest criticism and hidden gem spotlights.",
  });

  useJsonLd([
    {
      "@context":     "https://schema.org",
      "@type":        "Organization",
      "name":         "The Rough Cut",
      "url":          window.location.origin,
      "description":  "Independent indie game criticism. Honest reviews, hidden gem spotlights, and unfiltered opinions. No paid promotions.",
      "logo":         { "@type": "ImageObject", "url": `${window.location.origin}/RC.png` },
    },
    {
      "@context": "https://schema.org",
      "@type":    "WebSite",
      "name":     "The Rough Cut",
      "url":      window.location.origin,
    },
  ]);
  const [posts, setPosts] = useState([]);
  const [criticsPosts, setCriticsPosts] = useState([]);
  const [listPosts, setListPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/api/posts/?page_size=20"),
      api.get("/api/posts/?ordering=-view_count&page_size=8"),
      api.get("/api/posts/?is_list=true&page_size=3"),
    ])
      .then(([recentRes, picksRes, listsRes]) => {
        setPosts(recentRes.data.results ?? recentRes.data);
        setCriticsPosts(picksRes.data.results ?? picksRes.data);
        setListPosts(listsRes.data.results ?? listsRes.data);
      })
      .catch(() => {
        setPosts([]);
        setCriticsPosts([]);
        setListPosts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = (deletedId) => {
    setPosts(prev => prev.filter(p => p.id !== deletedId));
  };

  const [featured, ...rest] = posts;
  const latestCuts = rest.slice(0, 4);
  const criticsPicks = criticsPosts;
  const radar = rest.slice(4, 7).length > 0 ? rest.slice(4, 7) : rest.slice(0, 3);

  const marqueeItems = posts.length > 0
    ? posts.map(p => ({
        label: `${p.title}${p.category_detail?.name ? " — " + p.category_detail.name.toUpperCase() : ""}`,
        slug: p.slug,
      }))
    : [
        { label: "ROUGH CUT" },
        { label: "UNFILTERED OPINIONS" },
        { label: "NO FLUFF" },
        { label: "SHARP TAKES" },
      ];

  return (
    <div className="home-page">
      <h1 className="sr-only">The Rough Cut — Independent Indie Game Criticism</h1>

      {/* ── Marquee ─────────────────────────────────────────────────────────── */}
      <MarqueeBanner items={marqueeItems} />

      {/* ── Hero Featured ───────────────────────────────────────────────────── */}
      {loading ? <HeroSkeleton /> : featured && (
        <HeroFeatured post={featured} user={user} onDelete={handleDelete} />
      )}

      {/* ── Two-column: Latest Cuts + Critic's Picks ────────────────────────── */}
      {loading ? <LatestCutsSkeleton /> : latestCuts.length > 0 && (
        <section id="latest-cuts" className="home-two-col-section">
          <div className="home-two-col-inner">
            {/* Latest Cuts grid */}
            <div className="home-latest-col">
              <div className="home-section-banner">
                <h2 className="home-section-banner-title">
                  LATEST <span style={{ color: "var(--orange)" }}>CUTS</span>
                </h2>
                <span className="home-section-banner-sub mono">↗ RECENTLY POSTED</span>
              </div>
              <div className="home-latest-grid">
                {latestCuts.map((post, i) => (
                  <PostCard key={post.id} post={post} variant={i} user={user} onDelete={handleDelete} />
                ))}
              </div>
            </div>

            {/* Critic's Picks sidebar */}
            <CriticsPicks posts={criticsPicks} />
          </div>
        </section>
      )}

      {/* ── Newsletter ──────────────────────────────────────────────────────── */}
      <Newsletter />

      {/* ── On the Radar ────────────────────────────────────────────────────── */}
      {loading ? <RadarSkeleton /> : radar.length > 0 && (
        <section id="on-the-radar" className="home-radar-section">
          <div className="home-radar-inner">
            <div className="home-section-banner">
              <h2 className="home-section-banner-title">
                ON THE <span style={{ color: "var(--orange)" }}>RADAR</span>
              </h2>
              <span className="mono" style={{ color: "var(--orange)", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                ↗ More Posts
              </span>
            </div>
            <div className="home-radar-grid">
              {radar.map((post, i) => (
                <PostCard key={post.id} post={post} variant={i + 4} user={user} onDelete={handleDelete} />
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && posts.length === 0 && (
        <div className="home-empty">No posts yet — check back soon.</div>
      )}

      {/* ── Manifesto ───────────────────────────────────────────────────────── */}
      <Manifesto />

      {/* ── The Lists ───────────────────────────────────────────────────────── */}
      {loading ? <ListsSkeleton /> : listPosts.length > 0 && (
        <ListsPosts posts={listPosts} user={user} onDelete={(id) => setListPosts(prev => prev.filter(p => p.id !== id))} />
      )}

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <SiteFooter />
    </div>
  );
}

/* ── Marquee Banner ─────────────────────────────────────────────────────────── */
function MarqueeBanner({ items }) {
  // Repeat base items 6× per set so one set is always wider than any viewport.
  // Then 2 sets total so the -50% animation resets at a perfect boundary (seamless).
  const REPEATS = 6;
  const oneSet = Array.from({ length: REPEATS }, () => items).flat();
  const content = [...oneSet, ...oneSet];

  const trackRef = useRef(null);
  const [duration, setDuration] = useState(30);

  useEffect(() => {
    if (!trackRef.current) return;
    const measure = () => {
      const speed = 100; // px/s
      const oneSetWidth = trackRef.current.scrollWidth / 2;
      setDuration(Math.max(4, oneSetWidth / speed));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [items]);

  const itemStyle = {
    fontFamily: "'Archivo Black', sans-serif",
    fontSize: 18,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    margin: "0 24px",
    color: "var(--ink)",
    textDecoration: "none",
    display: "inline-block",
  };
  return (
    <div style={{
      overflow: "hidden",
      background: "var(--orange)",
      color: "var(--ink)",
      borderTop: "3px solid var(--ink)",
      borderBottom: "3px solid var(--ink)",
      padding: "10px 0",
      userSelect: "none",
    }}>
      <div className="marquee-track" ref={trackRef} style={{ animationDuration: `${duration}s` }}>
        {content.map((it, i) =>
          it.slug ? (
            <Link key={i} to={`/posts/${it.slug}`} style={itemStyle}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
            >
              {it.label} <span style={{ opacity: 0.5 }}>◆</span>
            </Link>
          ) : (
            <span key={i} style={itemStyle}>
              {it.label} <span style={{ opacity: 0.5 }}>◆</span>
            </span>
          )
        )}
      </div>
    </div>
  );
}

/* ── Hero Featured ──────────────────────────────────────────────────────────── */
function HeroFeatured({ post, user, onDelete }) {
  const navigate = useNavigate();
  const fallbackExcerpt = post.content?.blocks?.find(b => b.type === "paragraph")?.data?.text || "";
  const plainExcerpt = post.subtitle || stripHtml(fallbackExcerpt).slice(0, 200);
  const category = post.category_detail?.name;
  const author = post.author_detail?.username;
  const coAuthors_detail = post.co_authors_detail || [];
  const date = ordinalDate(post.publish_date || post.created_at);
  const tags = post.tags_detail || [];
  const verdictType = post.verdict ?? post.content?.blocks?.find(b => b.type === "verdict")?.data?.type;
  const isTestPost = (post.tags_detail || []).some(t => t.slug === "test");
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const isScheduled = user?.is_admin && post.publish_date && post.publish_date > today;

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/api/posts/${post.slug}/`);
      if (onDelete) onDelete(post.id);
    } catch {
      alert("Failed to delete post");
    }
  };

  return (
    <section className="home-hero-section">
      <div className="home-hero-inner">
        {/* Section header */}
        <div className="home-hero-header">
          <span className="mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: "var(--orange)", textTransform: "uppercase" }}>
            FEATURED POST
          </span>
          <span className="mono" style={{ fontSize: 11, color: "var(--gray)", letterSpacing: "0.1em" }}>
            {date}
            {(author || coAuthors_detail.length > 0) && <> · BY </>}
            {author && (post.author_detail?.has_author_page
              ? <Link to={`/authors/${author}`} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}>{author.toUpperCase()}</Link>
              : author.toUpperCase()
            )}
            {coAuthors_detail.map((ca, i) => (
              <Fragment key={ca.id}>
                {(author || i > 0) ? " & " : ""}
                {ca.has_author_page
                  ? <Link to={`/authors/${ca.username}`} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}>{ca.username.toUpperCase()}</Link>
                  : ca.username.toUpperCase()}
              </Fragment>
            ))}
          </span>
        </div>

        {/* Hero grid */}
        <div className="home-hero-grid">
          {/* Left — image */}
          <div className="home-hero-image-wrap">
            <Link to={`/posts/${post.slug}`} style={{ display: "block" }}>
              <div className="home-hero-img-inner">
                {post.featured_image ? (
                  <img
                    src={cloudinaryImg(post.featured_image, { width: 1280 })}
                    alt={post.title}
                    fetchpriority="high"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "cover",
                      filter: "grayscale(15%)",
                      transition: "filter 0.2s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.filter = "grayscale(0%)"; }}
                    onMouseLeave={e => { e.currentTarget.style.filter = "grayscale(15%)"; }}
                  />
                ) : (
                  <div className="home-hero-placeholder">
                    <div className="home-hero-placeholder-inner">
                      {post.title?.slice(0, 2).toUpperCase()}
                    </div>
                  </div>
                )}
              </div>
            </Link>
            {verdictType ? (
              <div style={{ position: "absolute", bottom: -20, right: -10, zIndex: 4 }}>
                <VerdictStamp type={verdictType} size="lg" rotate={-4} />
              </div>
            ) : category ? (
              <div className="home-hero-category-badge">{category}</div>
            ) : null}
          </div>

          {/* Right — content */}
          <div className="home-hero-content">
            {user?.is_admin && (isTestPost || isScheduled) && (
              <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                {isTestPost && (
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, fontWeight: 700,
                    letterSpacing: "2px", textTransform: "uppercase",
                    background: "var(--orange)", color: "var(--ink)",
                    padding: "3px 8px",
                  }}>{'// TEST POST'}</span>
                )}
                {isScheduled && (
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, fontWeight: 700,
                    letterSpacing: "2px", textTransform: "uppercase",
                    color: "var(--orange)",
                    border: "1.5px solid var(--orange)",
                    padding: "3px 8px",
                  }}>◷ SCHEDULED</span>
                )}
              </div>
            )}
            {tags.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginBottom: 0, flexWrap: "wrap" }}>
                {tags.slice(0, 4).map(t => (
                  <span key={t.id} style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    color: "var(--orange)",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    border: "1.5px solid var(--orange)",
                    padding: "2px 8px",
                  }}>
                    {t.name}
                  </span>
                ))}
              </div>
            )}

            <h2 className="home-hero-title display">
              <Link to={`/posts/${post.slug}`} style={{ color: "inherit", textDecoration: "none" }}>
                {post.title}
              </Link>
            </h2>

            {plainExcerpt && (
              <p className="home-hero-excerpt">
                {plainExcerpt}{!post.subtitle && "…"}
              </p>
            )}

            <div className="home-hero-meta-strip mono">
              {(author || coAuthors_detail.length > 0) && (
                <div>
                  <span style={{ color: "var(--gray)" }}>BY /</span>{" "}
                  {author && (post.author_detail?.has_author_page
                    ? <Link to={`/authors/${author}`} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}>{author.toUpperCase()}</Link>
                    : author.toUpperCase()
                  )}
                  {coAuthors_detail.map((ca, i) => (
                    <Fragment key={ca.id}>
                      {(author || i > 0) ? " & " : ""}
                      {ca.has_author_page
                        ? <Link to={`/authors/${ca.username}`} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}>{ca.username.toUpperCase()}</Link>
                        : ca.username.toUpperCase()}
                    </Fragment>
                  ))}
                </div>
              )}
              {category && <div><span style={{ color: "var(--gray)" }}>TOPIC /</span> {category.toUpperCase()}</div>}
              <div><span style={{ color: "var(--gray)" }}>DATE /</span> {date.toUpperCase()}</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Link to={`/posts/${post.slug}`} className="home-hero-cta">
                READ THE FULL POST →
              </Link>
              {user?.is_admin && (user?.is_super_editor || post.created_by === user.id || (post.co_authors || []).includes(user.id)) && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => navigate(`/editor/${post.slug}`)}
                    style={adminBtnStyle}
                    title="Edit"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--orange)"; e.currentTarget.style.color = "var(--orange)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--gray-dim)"; e.currentTarget.style.color = "var(--paper)"; }}
                  >{"// EDIT"}</button>
                  <button
                    onClick={handleDelete}
                    style={adminBtnStyle}
                    title="Delete"
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--orange)"; e.currentTarget.style.color = "var(--orange)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--gray-dim)"; e.currentTarget.style.color = "var(--paper)"; }}
                  >✕ DEL</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const adminBtnStyle = {
  background: "var(--ink)",
  border: "1px solid var(--gray-dim)",
  padding: "5px 10px",
  cursor: "pointer",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.65rem",
  letterSpacing: "0.05em",
  color: "var(--paper)",
  transition: "border-color 0.15s, color 0.15s",
};

/* ── Critic's Picks Sidebar ─────────────────────────────────────────────────── */
function CriticsPicks({ posts }) {
  const navigate = useNavigate();
  return (
    <aside className="home-critics-picks paper">
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{
          display: "inline-block",
          background: "var(--ink)",
          color: "var(--orange)",
          padding: "6px 14px",
          fontFamily: "'Archivo Black', sans-serif",
          fontSize: 18,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          transform: "rotate(-2deg)",
          marginBottom: 20,
        }}>
          ◆ CRITIC'S PICKS
        </div>
        <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {posts.slice(0, 8).map((post, i, arr) => (
            <li
              key={post.id}
              onClick={() => navigate(`/posts/${post.slug}`)}
              style={{
                display: "flex",
                gap: 14,
                padding: "12px 0",
                borderBottom: i < arr.length - 1 ? "1px dashed var(--ink)" : "none",
                cursor: "pointer",
              }}
            >
              <div className="display" style={{
                fontSize: 36,
                color: "var(--orange)",
                lineHeight: 0.8,
                textShadow: "2px 2px 0 var(--ink)",
                flexShrink: 0,
                width: 40,
              }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div style={{ minWidth: 0 }}>
                <h4 className="slab" style={{
                  fontSize: 15,
                  lineHeight: 1.1,
                  margin: "0 0 3px",
                  color: "var(--ink)",
                }}>
                  {post.title}
                </h4>
                <div className="mono" style={{
                  fontSize: 10,
                  color: "var(--gray-dim)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}>
                  {post.author_detail?.has_author_page
                    ? <Link to={`/authors/${post.author_detail.username}`} onClick={e => e.stopPropagation()} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}>{post.author_detail.username}</Link>
                    : post.author_detail?.username}
                  {post.category_detail?.name ? " · " + post.category_detail.name : ""}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </aside>
  );
}

/* ── Newsletter ──────────────────────────────────────────────────────────────── */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [focused, setFocused] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [cursorX, setCursorX] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);
  const rulerRef = useRef(null);

  useEffect(() => {
    if (focused && rulerRef.current) {
      rulerRef.current.textContent = email.slice(0, cursorPos);
      setCursorX(rulerRef.current.offsetWidth);
    }
  }, [email, focused, cursorPos]);

  const trackCursor = (e) => setCursorPos(e.target.selectionStart ?? e.target.value.length);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const res = await api.post("/api/subscribe/", { email });
      setMessage(res.data.message);
      setStatus("success");
      setEmail("");
    } catch (err) {
      setMessage(err.response?.data?.error || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section className="home-newsletter">
      {/* Halftone bg */}
      <div style={{
        position: "absolute",
        inset: 0,
        background: "radial-gradient(circle at 2px 2px, rgba(10,10,10,0.15) 1.5px, transparent 2px)",
        backgroundSize: "8px 8px",
        pointerEvents: "none",
      }} />
      <div className="home-newsletter-inner">
        <div>
          <div style={{
            display: "inline-block",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            background: "var(--ink)",
            color: "var(--orange)",
            padding: "4px 10px",
            marginBottom: 16,
          }}>
            ROUGH CUT // DISPATCH
          </div>
          <h2 className="display home-newsletter-heading">
            <div>ONE EMAIL.</div>
            <div style={{ marginTop: 10 }}>
              <span style={{
                display: "inline-block",
                background: "var(--ink)",
                color: "var(--orange)",
                padding: "4px 12px",
              }}>EVERY</span>{" "}
              <span style={{
                display: "inline-block",
                background: "var(--ink)",
                color: "var(--orange)",
                padding: "4px 12px",
                marginTop: 6,
              }}>FRIDAY.</span>
            </div>
          </h2>
          <p style={{
            marginTop: 24,
            fontSize: 17,
            color: "var(--ink)",
            maxWidth: 440,
            fontWeight: 500,
            lineHeight: 1.6,
          }}>
            
          </p>
        </div>
        <div style={{
          background: "var(--ink)",
          padding: 28,
          border: "3px solid var(--ink)",
          boxShadow: "8px 8px 0 rgba(10,10,10,0.3)",
          transform: "rotate(1deg)",
        }}>
          <label className="mono" style={{
            display: "block",
            fontSize: 11,
            color: "var(--orange)",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}>
            &gt; EMAIL_ADDRESS:
          </label>
          <div style={{ position: "relative" }}>
            {/* Hidden ruler to measure typed text width */}
            <span
              ref={rulerRef}
              aria-hidden="true"
              style={{
                position: "absolute",
                visibility: "hidden",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 16,
                whiteSpace: "pre",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              inputMode="email"
              autoComplete="email"
              placeholder={focused ? "" : "you@somewhere.net"}
              value={email}
              onChange={e => { setEmail(e.target.value); trackCursor(e); }}
              onSelect={trackCursor}
              onClick={trackCursor}
              onKeyUp={trackCursor}
              onFocus={e => { setFocused(true); trackCursor(e); }}
              onBlur={() => setFocused(false)}
              style={{
                width: "100%",
                padding: "14px 16px",
                background: "var(--ink-2)",
                color: "var(--paper)",
                border: "2px solid var(--orange)",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 16,
                outline: "none",
                boxSizing: "border-box",
                caretColor: "transparent",
              }}
            />
            {focused && (
              <span style={{
                position: "absolute",
                left: 16 + cursorX,
                top: "50%",
                transform: "translateY(-50%)",
                width: 2,
                height: 18,
                background: "var(--orange)",
                display: "block",
                pointerEvents: "none",
                animation: "blink-cursor 0.7s step-end infinite",
              }} />
            )}
          </div>
          <button
            className="btn btn-solid newsletter-signup-btn"
            style={{ marginTop: 14, width: "100%", justifyContent: "center", fontSize: 14, padding: "14px" }}
            onClick={handleSubmit}
            disabled={status === "loading"}
          >
            {status === "loading" ? "SENDING…" : "SIGN ME UP ↗︎"}
          </button>

          {message && (
            <div className="mono" style={{
              marginTop: 10,
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              textAlign: "center",
              color: status === "error" ? "#ff5a1f" : "#4ade80",
              padding: "8px",
              border: `1px solid ${status === "error" ? "#ff5a1f" : "#4ade80"}`,
              background: "rgba(0,0,0,0.3)",
            }}>
              {status === "error" ? "✕ " : "✓ "}{message}
            </div>
          )}

          {!message && (
            <div className="mono" style={{
              marginTop: 12,
              fontSize: 10,
              color: "var(--gray)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              textAlign: "center",
            }}>
              UNSUBSCRIBE WHENEVER · WE'RE NOT HURT
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Manifesto ───────────────────────────────────────────────────────────────── */
function Manifesto() {
  return (
    <section className="home-manifesto paper">
      <div style={{ maxWidth: 860, margin: "0 auto", position: "relative", zIndex: 2, textAlign: "center" }}>
        <div className="mono" style={{
          fontSize: 11,
          color: "var(--orange)",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          marginBottom: 16,
        }}>
          {"// THE MANIFESTO"}
        </div>
        <p style={{ fontSize: 22, lineHeight: 1.5, color: "var(--ink-2)", marginBottom: 20 }}>
          We write about <strong>indie games worth writing about</strong>.
          No sponsored content. Just writers who enjoy writing about video games.
        </p>
        <p style={{
          color: "var(--orange)",
          fontFamily: "'Archivo Black', sans-serif",
          fontSize: 24,
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        }}>
          Rough cuts only. Polish is a lie.
        </p>
      </div>
    </section>
  );
}

/* ── The Lists Section ───────────────────────────────────────────────────────── */
function ListsPosts({ posts, user, onDelete }) {
  return (
    <section id="the-lists" className="home-lists-section">
      <div className="home-lists-inner">
        <div className="home-section-banner">
          <h2 className="home-section-banner-title">
            THE <span style={{ color: "var(--orange)" }}>LISTS</span>
          </h2>
          <span className="mono" style={{ color: "var(--orange)", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            {"// TOP 10s & RANKINGS"}
          </span>
        </div>
        <div className="home-lists-grid">
          {posts.map((post, i) => (
            <PostCard key={post.id} post={post} variant={i} user={user} onDelete={onDelete} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Skeleton helpers ────────────────────────────────────────────────────────── */

function S({ w = "100%", h = 16, style = {} }) {
  return <div className="skel" style={{ width: w, height: h, ...style }} />;
}

function PostCardSkeleton() {
  return (
    <div className="skel-card">
      <div className="skel skel-card-image" />
      <div className="skel-card-lines">
        <S h={18} w="90%" />
        <S h={18} w="70%" />
        <S h={13} w="100%" style={{ marginTop: 4 }} />
        <S h={13} w="85%" />
      </div>
      <div className="skel-card-meta">
        <S h={10} w="25%" />
        <S h={10} w="20%" />
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <section className="skel-hero">
      <div className="skel-hero-inner">
        <div className="skel-hero-header">
          <S h={11} w={120} />
          <S h={11} w={180} />
        </div>
        <div className="skel-hero-grid">
          <div className="skel skel-hero-image" />
          <div className="skel-hero-content">
            <div className="skel-hero-tags">
              <S h={22} w={70} />
              <S h={22} w={90} />
            </div>
            <S h={90} w="95%" />
            <S h={80} w="80%" />
            <S h={22} w="70%" />
            <div className="skel-hero-meta">
              <S h={11} w={80} />
              <S h={11} w={100} />
              <S h={11} w={80} />
            </div>
            <S h={50} w={220} />
          </div>
        </div>
      </div>
    </section>
  );
}

function LatestCutsSkeleton() {
  return (
    <section className="skel-two-col">
      <div className="skel-two-col-inner">
        <div>
          <div className="skel-banner">
            <S h={50} w={260} />
            <S h={11} w={120} />
          </div>
          <div className="skel-latest-grid">
            {[0,1,2,3].map(i => <PostCardSkeleton key={i} />)}
          </div>
        </div>
        <div className="skel-picks">
          <S h={34} w={180} style={{ marginBottom: 20, transform: "rotate(-2deg)" }} />
          {[0,1,2,3,4,5,6,7].map(i => (
            <div className="skel-pick-item" key={i}>
              <S h={36} w={40} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <S h={14} w="90%" />
                <S h={10} w="60%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RadarSkeleton() {
  return (
    <section className="skel-radar">
      <div className="skel-radar-inner">
        <div className="skel-banner">
          <S h={50} w={280} />
          <S h={11} w={100} />
        </div>
        <div className="skel-radar-grid">
          {[0,1,2].map(i => <PostCardSkeleton key={i} />)}
        </div>
      </div>
    </section>
  );
}

function ListsSkeleton() {
  return (
    <section className="skel-lists">
      <div className="skel-lists-inner">
        <div className="skel-banner">
          <S h={50} w={240} />
          <S h={11} w={160} />
        </div>
        <div className="skel-lists-grid">
          {[0,1,2].map(i => <PostCardSkeleton key={i} />)}
        </div>
      </div>
    </section>
  );
}
